import { FC, useEffect, useState, useRef } from 'react';
import { Box, Skeleton } from '@mui/material';

import { AnetCustomerProfileDto } from '@libs/dto';
import { PaymentCard } from '@carcareers/components';
import { useForm } from '@carcareers/contexts';
import { paymentApi } from '@carcareers/store';

interface PaymentProps {
  onClick?: (paymentId: string) => void;
  onClickIsNew?: () => void;
  isInput?: boolean;
  size?: 'small' | 'medium';
  fieldName?: string;
}

export const Payment: FC<PaymentProps> = ({
  onClick,
  onClickIsNew,
  isInput = false,
  size = 'medium',
  fieldName = 'paymentProfileId',
}) => {
  const [profile, setProfile] = useState<AnetCustomerProfileDto>();
  const [selectedPayment, setSelectedPayment] = useState<string>();
  const { data } = useForm<{ paymentProfileId: string }>();
  const { data: paymentData, isLoading } = paymentApi.useGetCustomerProfileQuery();
  const hasInitializedRef = useRef(false);
  
  const fieldValue = data?.[fieldName as keyof typeof data];

  useEffect(() => {
    if (!isLoading && paymentData) {
      setProfile(paymentData);
      if (!selectedPayment && paymentData?.profile?.paymentProfiles?.[0]) {
        setSelectedPayment(paymentData.profile.paymentProfiles[0].customerPaymentProfileId);
      }
    }
  }, [paymentData, isLoading, selectedPayment]);

  useEffect(() => {
    if (isInput && selectedPayment && !fieldValue && !hasInitializedRef.current) {
      onClick?.(selectedPayment);
      hasInitializedRef.current = true;
    }
  }, [selectedPayment, isInput, fieldValue]);

  if (isLoading) {
    return <Skeleton variant="rectangular" height={200} width={300} sx={{ borderRadius: 2 }} />;
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      {profile?.profile?.paymentProfiles?.map((paymentProfile) => (
        <PaymentCard
          size={size}
          key={paymentProfile.customerPaymentProfileId}
          isInput={isInput}
          customerPaymentProfileId={paymentProfile.customerPaymentProfileId}
          cardNumber={paymentProfile.payment.creditCard.cardNumber}
          expirationDate={paymentProfile.payment.creditCard.expirationDate}
          cardType={paymentProfile.payment.creditCard.cardType}
          defaultPaymentProfile={paymentProfile.defaultPaymentProfile}
          selected={isInput ? selectedPayment === paymentProfile.customerPaymentProfileId : false}
          onClick={() => {
            if (isInput) {
              onClick?.(paymentProfile.customerPaymentProfileId);
              setSelectedPayment(paymentProfile.customerPaymentProfileId);
            }
          }}
        />
      ))}
      {!isInput && <PaymentCard isNew onClick={onClickIsNew} />}
    </Box>
  );
};

export default Payment;
